import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { OrdersService } from '../../../../core/services/orders/orders.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-duplicate-into-offer-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NgSelectModule],
  templateUrl: './duplicate-into-offer-dialog.component.html',
  styleUrl: './duplicate-into-offer-dialog.component.css'
})
export class DuplicateIntoOfferDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() orderData: any;
  @Input() ladata: any;
  @Output() close = new EventEmitter<void>();
  @Output() duplicated = new EventEmitter<any>();

  mode: 'campaign' | 'custom' | null = null;
  submitting = false;
  durationOptions: string[] = ['12', '24', '36', '48', '60', '72'];
  typeOptions: string[] = ['renting', 'leasing'];

  // Campaign mode
  campaignsData: any[] = [];
  selectedCampaignRef: string = '';
  filteredCampaigns: any[] = [];
  showCampaignDropdown = false;
  displayCampaignText = '';

  // Custom offer mode
  offerFields = {
    monthly_fee_base: null as number | null,
    monthly_fee_tax: null as number | null,
    monthly_fee_total: null as number | null,
    duration: null as number | null,
    yearly_kms: null as number | null,
    total_kms: null as number | null,
    type: null as string | null,
    deposit: null as number | null,
    initial_payment: null as number | null,
    transport: null as number | null,
    prepayment: null as number | null,
    include_maintenance: null as boolean | null,
    include_insurance: null as boolean | null,
    include_tires: null as boolean | null,
    include_replacement_vehicle: null as boolean | null,
  };

  get taxRate(): number {
    return this.authStateService.getSupplierTax() || 0.21;
  }

  constructor(
    private orderService: OrdersService,
    private alertService: AlertService,
    private authStateService: AuthStateService
  ) {}

  ngOnInit(): void {
    this.prefillOfferFields();
    this.campaignsData = this.ladata?.campaignData || [];
    this.filteredCampaigns = [...this.campaignsData];
  }

  prefillOfferFields(): void {
    if (!this.orderData) return;
    const fields = Object.keys(this.offerFields) as (keyof typeof this.offerFields)[];
    for (const key of fields) {
      const value = this.orderData[key];
      if (value !== undefined && value !== null) {
        if (typeof this.offerFields[key] === 'boolean' || key.startsWith('include_')) {
          (this.offerFields as any)[key] = !!value;
        } else if (key === 'type') {
          (this.offerFields as any)[key] = value;
        } else {
          (this.offerFields as any)[key] = parseFloat(value);
        }
      }
    }
  }

  setMode(mode: 'campaign' | 'custom'): void {
    this.mode = mode;
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (this.mode === 'campaign') return !!this.selectedCampaignRef;
    if (this.mode === 'custom') return true;
    return false;
  }

  // Campaign selector
  onCampaignFocus(): void {
    this.showCampaignDropdown = true;
    this.filteredCampaigns = [...this.campaignsData];
  }

  onCampaignBlur(): void {
    setTimeout(() => {
      this.showCampaignDropdown = false;
    }, 200);
  }

  searchCampaigns(target: any): void {
    const search = (target.value || '').toLowerCase();
    this.filteredCampaigns = this.campaignsData.filter((c: any) =>
      (c.name || '').toLowerCase().includes(search) ||
      (c.supplier_name || '').toLowerCase().includes(search)
    );
  }

  selectCampaign(campaign: any): void {
    this.selectedCampaignRef = campaign.reference;
    this.displayCampaignText = (campaign.supplier_name ? campaign.supplier_name + ' - ' : '') + campaign.name;
    this.showCampaignDropdown = false;
  }

  clearCampaign(): void {
    this.selectedCampaignRef = '';
    this.displayCampaignText = '';
  }

  // Cálculos de cuotas y kilómetros (misma lógica que contract-form)
  onMonthlyFeeBaseChange(): void {
    const base = parseFloat(this.offerFields.monthly_fee_base as any);
    if (!isNaN(base)) {
      const baseRounded = parseFloat(base.toFixed(2));
      this.offerFields.monthly_fee_base = baseRounded;
      this.offerFields.monthly_fee_tax = parseFloat((baseRounded * this.taxRate).toFixed(2));
      this.offerFields.monthly_fee_total = parseFloat((baseRounded * (1 + this.taxRate)).toFixed(2));
    }
  }

  onMonthlyFeeTaxChange(): void {
    const tax = parseFloat(this.offerFields.monthly_fee_tax as any);
    if (!isNaN(tax)) {
      const taxRounded = parseFloat(tax.toFixed(2));
      this.offerFields.monthly_fee_tax = taxRounded;
      this.offerFields.monthly_fee_base = parseFloat((taxRounded / this.taxRate).toFixed(2));
      this.offerFields.monthly_fee_total = parseFloat(((taxRounded / this.taxRate) * (1 + this.taxRate)).toFixed(2));
    }
  }

  onMonthlyFeeTotalChange(): void {
    const total = parseFloat(this.offerFields.monthly_fee_total as any);
    if (!isNaN(total)) {
      const totalRounded = parseFloat(total.toFixed(2));
      this.offerFields.monthly_fee_total = totalRounded;
      this.offerFields.monthly_fee_base = parseFloat((totalRounded / (1 + this.taxRate)).toFixed(2));
      this.offerFields.monthly_fee_tax = parseFloat(((totalRounded / (1 + this.taxRate)) * this.taxRate).toFixed(2));
    }
  }

  onYearlyKmsChange(): void {
    const kms = parseInt(this.offerFields.yearly_kms as any);
    const months = parseInt(this.offerFields.duration as any);
    if (!isNaN(kms) && !isNaN(months)) {
      this.offerFields.total_kms = Math.round(kms * (months / 12));
    }
  }

  onTotalKmsChange(): void {
    const kms = parseInt(this.offerFields.total_kms as any);
    const months = parseInt(this.offerFields.duration as any);
    if (!isNaN(kms) && !isNaN(months)) {
      this.offerFields.yearly_kms = Math.round((kms / months) * 12);
    }
  }

  onDurationChange(): void {
    const kms = parseInt(this.offerFields.yearly_kms as any);
    const months = parseInt(this.offerFields.duration as any);
    if (!isNaN(kms) && !isNaN(months)) {
      this.offerFields.total_kms = Math.round(kms * (months / 12));
    }
  }

  getMinFee(campaign: any): number | null {
    const matrix = campaign?.matrix_total || campaign?.matrix_base;
    if (!matrix) return null;
    const values = Object.values(matrix).flatMap(row => Object.values(row as object)) as number[];
    return values.length ? Math.min(...values) : null;
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.submitting) return;

    let body: any = {};
    if (this.mode === 'campaign' && this.selectedCampaignRef) {
      body = { campaign: this.selectedCampaignRef };
    } else if (this.mode === 'custom') {
      const entries = Object.entries(this.offerFields) as [string, any][];
      for (const [key, value] of entries) {
        if (value !== null && value !== undefined && value !== '') {
          body[key] = value;
        }
      }
    }

    // Round monetary fields to 2 decimals before sending
    for (const field of ['monthly_fee_base', 'monthly_fee_tax', 'monthly_fee_total']) {
      if (body[field] !== null && body[field] !== undefined && body[field] !== '') {
        body[field] = parseFloat(parseFloat(body[field]).toFixed(2));
      }
    }

    this.submitting = true;
    this.alertService.loading('swal_messages.please_wait');

    this.orderService.duplicateIntoOffer(this.orderData.reference, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.submitting = false;
          this.alertService.successOk('swal_messages.duplicate_into_offer_success')
            .then((result) => {
              if (result.isConfirmed) {
                this.duplicated.emit(response.data);
              }
            });
        },
        error: (error) => {
          this.submitting = false;
          const errors = error?.error?.errors;
          if (errors) {
            const firstKey = Object.keys(errors)[0];
            const msg = errors[firstKey]?.[0]?.msg || 'swal_messages.general.error';
            this.alertService.error(msg);
          } else {
            this.alertService.error('swal_messages.general.error');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
